import { stableStringify } from "./relaySigning.ts";

/** Exact originals use the existing immutable World snapshot object door.
 * Bodies are stored once per version; 64-way indexes locate a message or text
 * byte range without replaying the conversation or loading unrelated bodies. */
export const ORIGINAL_OBJECT_MAX_BYTES = 512 * 1024;
export const ORIGINAL_TEXT_BYTES = 64 * 1024;
const FANOUT = 64;
const SCHEMA = "conversation-original-v1";
const segment = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u;
const digestPattern = /^[a-f0-9]{64}$/u;
const utf8 = new TextEncoder();
const text = new TextDecoder("utf-8", { fatal: true, ignoreBOM: true });
export interface OriginalCoordinate {
  worldKey: string;
  roomId: string;
  sessionId: string;
  threadId: string;
}
export interface OriginalRef {
  digest: string;
  count: number;
}
export interface OriginalMessageMetadata {
  id: string;
  role: string;
  attachments?: readonly unknown[];
  turnId: string | null;
  streaming: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface OriginalSource {
  rootEventId: string;
  throughSequence: number;
  throughStreamVersion: number;
  rangeDigest: string;
}
export interface OriginalMessageEntry {
  original: OriginalMessageMetadata;
  version: string;
  text: OriginalRef;
}
type Part =
  | { kind: "text"; content: string }
  | { kind: "text-index" | "message-index"; entries: OriginalRef[] }
  | { kind: "messages"; entries: OriginalMessageEntry[] }
  | { kind: "range"; revision: string; source: OriginalSource; messages: OriginalRef };
export type OriginalObject = OriginalCoordinate & { schemaVersion: typeof SCHEMA } & Part;
const natural = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 0;
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const validRef = (value: unknown): value is OriginalRef =>
  record(value) &&
  Object.keys(value).length === 2 &&
  typeof value.digest === "string" &&
  digestPattern.test(value.digest) &&
  natural(value.count);
const refuse = (reason: string): never => {
  throw Error(`Original messages: ${reason}`);
};

export function originalObjectMemberId(
  coordinate: Omit<OriginalCoordinate, "worldKey">,
  digest: string,
  range = false,
) {
  if (
    ![coordinate.roomId, coordinate.sessionId, coordinate.threadId].every((s) => segment.test(s)) ||
    !digestPattern.test(digest)
  )
    return refuse("invalid source identity");
  return `world-r2:context/sessions/${coordinate.roomId}/${coordinate.sessionId}/${coordinate.threadId}/snapshots/original-${range ? "range" : "object"}-${digest}.md`;
}
export function parseWorldOriginalMemberId(memberId: string) {
  const match =
    /^world-r2:context\/sessions\/([^/]+)\/([^/]+)\/([^/]+)\/snapshots\/original-range-([a-f0-9]{64})\.md(?:\/(?:original\/(0|[1-9]\d*)|index\/(0|[1-9]\d*)))?$/u.exec(
      memberId,
    );
  if (!match || memberId.length > 600 || !match.slice(1, 4).every((s) => segment.test(s!)))
    return null;
  const coordinate = { roomId: match[1]!, sessionId: match[2]!, threadId: match[3]! };
  const ordinal = match[5] === undefined ? null : Number(match[5]);
  const indexStart = Number(match[6] ?? 0);
  if ((ordinal !== null && !natural(ordinal)) || !natural(indexStart)) return null;
  return {
    ...coordinate,
    digest: match[4]!,
    rootMemberId: originalObjectMemberId(coordinate, match[4]!, true),
    ordinal,
    indexStart,
  };
}
export const originalObjectBytes = (value: OriginalObject) => utf8.encode(stableStringify(value));
export async function originalObjectDigest(bytes: Uint8Array) {
  return [
    ...new Uint8Array(await crypto.subtle.digest("SHA-256", bytes as Uint8Array<ArrayBuffer>)),
  ]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}
export function originalObjectCount(value: OriginalObject): number {
  if (value.kind === "text") return utf8.encode(value.content).length;
  if (value.kind === "messages") return value.entries.length;
  if (value.kind === "range") return value.messages.count;
  const total = value.entries.reduce((sum, entry) => sum + entry.count, 0);
  if (!natural(total)) return refuse("index size overflow");
  return total;
}
export function decodeOriginalObject(
  bytes: Uint8Array,
  coordinate: OriginalCoordinate,
): OriginalObject {
  if (bytes.length > ORIGINAL_OBJECT_MAX_BYTES) return refuse("object exceeds its read budget");
  let value: unknown;
  try {
    value = JSON.parse(text.decode(bytes));
  } catch {
    return refuse("invalid stored object");
  }
  if (
    !record(value) ||
    value.schemaVersion !== SCHEMA ||
    !Object.entries(coordinate).every(([key, expected]) => value[key] === expected)
  )
    return refuse("source belongs to another conversation or World");
  const keys = ["schemaVersion", "worldKey", "roomId", "sessionId", "threadId", "kind"];
  const exact = (...extra: string[]) =>
    Object.keys(value).length === keys.length + extra.length &&
    Object.keys(value).every((key) => keys.includes(key) || extra.includes(key));
  if (value.kind === "text") {
    if (
      !exact("content") ||
      typeof value.content !== "string" ||
      utf8.encode(value.content).length > ORIGINAL_TEXT_BYTES ||
      /[\uD800-\uDFFF]/u.test(value.content)
    )
      return refuse("invalid original text chunk");
  } else if (value.kind === "text-index" || value.kind === "message-index") {
    if (
      !exact("entries") ||
      !Array.isArray(value.entries) ||
      value.entries.length < 1 ||
      value.entries.length > FANOUT ||
      !value.entries.every(validRef)
    )
      return refuse("invalid original index");
  } else if (value.kind === "messages") {
    if (
      !exact("entries") ||
      !Array.isArray(value.entries) ||
      value.entries.length < 1 ||
      value.entries.length > FANOUT
    )
      return refuse("invalid original message page");
    for (const entry of value.entries) {
      if (!record(entry) || Object.keys(entry).length !== 3)
        return refuse("invalid original message entry");
      const m = entry.original;
      if (
        !record(m) ||
        typeof m.id !== "string" ||
        !m.id ||
        typeof m.role !== "string" ||
        typeof m.createdAt !== "string" ||
        !Number.isFinite(Date.parse(m.createdAt)) ||
        typeof m.updatedAt !== "string" ||
        !Number.isFinite(Date.parse(m.updatedAt)) ||
        (m.turnId !== null && typeof m.turnId !== "string") ||
        typeof m.streaming !== "boolean" ||
        (m.attachments !== undefined && !Array.isArray(m.attachments)) ||
        Object.keys(m).some(
          (k) =>
            ![
              "id",
              "role",
              "attachments",
              "turnId",
              "streaming",
              "createdAt",
              "updatedAt",
            ].includes(k),
        ) ||
        typeof entry.version !== "string" ||
        !digestPattern.test(entry.version) ||
        !validRef(entry.text)
      )
        return refuse("invalid original message metadata");
    }
  } else if (value.kind === "range") {
    const s = value.source;
    if (
      !exact("revision", "source", "messages") ||
      typeof value.revision !== "string" ||
      !segment.test(value.revision) ||
      !validRef(value.messages) ||
      value.messages.count < 1 ||
      !record(s) ||
      Object.keys(s).length !== 4 ||
      typeof s.rootEventId !== "string" ||
      !s.rootEventId ||
      !natural(s.throughSequence) ||
      !natural(s.throughStreamVersion) ||
      typeof s.rangeDigest !== "string" ||
      !digestPattern.test(s.rangeDigest)
    )
      return refuse("invalid original range");
  } else return refuse("unknown original object kind");
  originalObjectCount(value as unknown as OriginalObject);
  return value as unknown as OriginalObject;
}

/** store must durably write and independently verify each immutable object.
 * Parents are published only after all of their children have succeeded. */
export async function preserveWorldOriginalRange(input: {
  coordinate: OriginalCoordinate;
  revision: string;
  source: OriginalSource;
  messages: readonly { original: OriginalMessageMetadata; text: string; version: string }[];
  store: (memberId: string, bytes: Uint8Array) => Promise<void>;
}) {
  const saved = new Map<string, OriginalRef>();
  const store = async (part: Part): Promise<OriginalRef> => {
    const value: OriginalObject = { schemaVersion: SCHEMA, ...input.coordinate, ...part };
    const bytes = originalObjectBytes(value);
    decodeOriginalObject(bytes, input.coordinate);
    const digest = await originalObjectDigest(bytes),
      count = originalObjectCount(value);
    if (!saved.has(digest)) {
      await input.store(
        originalObjectMemberId(input.coordinate, digest, part.kind === "range"),
        bytes,
      );
      saved.set(digest, { digest, count });
    }
    return { digest, count };
  };
  const index = async (kind: "text-index" | "message-index", references: OriginalRef[]) => {
    if (!references.length) return refuse("cannot publish an empty index");
    let level = references;
    while (level.length > 1) {
      const next: OriginalRef[] = [];
      for (let start = 0; start < level.length; start += FANOUT)
        next.push(await store({ kind, entries: level.slice(start, start + FANOUT) }));
      level = next;
    }
    return level[0]!;
  };
  const messages: OriginalRef[] = [];
  let entries: OriginalMessageEntry[] = [];
  for (const message of input.messages) {
    if (/[\uD800-\uDFFF]/u.test(message.text)) return refuse("invalid Unicode in original message");
    const bytes = utf8.encode(message.text),
      chunks: OriginalRef[] = [];
    for (let start = 0; start < bytes.length || (start === 0 && bytes.length === 0);) {
      let end = Math.min(start + ORIGINAL_TEXT_BYTES, bytes.length);
      while (end < bytes.length && (bytes[end]! & 0xc0) === 0x80) end--;
      chunks.push(await store({ kind: "text", content: text.decode(bytes.subarray(start, end)) }));
      start = end;
      if (start === bytes.length) break;
    }
    entries.push({
      original: message.original,
      version: message.version,
      text: await index("text-index", chunks),
    });
    if (entries.length === FANOUT) {
      messages.push(await store({ kind: "messages", entries }));
      entries = [];
    }
  }
  if (entries.length) messages.push(await store({ kind: "messages", entries }));
  const root = await store({
    kind: "range",
    revision: input.revision,
    source: input.source,
    messages: await index("message-index", messages),
  });
  return {
    memberId: originalObjectMemberId(input.coordinate, root.digest, true),
    digest: root.digest,
    messageCount: root.count,
  };
}

export async function readWorldOriginalRange(input: {
  worldKey: string;
  memberId: string;
  range: { startByte: number; maxBytes: number; version?: string };
  read: (memberId: string) => Promise<Uint8Array>;
}) {
  const parsed = parseWorldOriginalMemberId(input.memberId);
  if (!parsed) return refuse("invalid original reference");
  const coordinate = {
    worldKey: input.worldKey,
    roomId: parsed.roomId,
    sessionId: parsed.sessionId,
    threadId: parsed.threadId,
  };
  const cache = new Map<string, OriginalObject>();
  const load = async (reference: OriginalRef, root = false) => {
    let value = cache.get(reference.digest);
    if (!value) {
      const bytes = await input.read(originalObjectMemberId(coordinate, reference.digest, root));
      if ((await originalObjectDigest(bytes)) !== reference.digest)
        return refuse("stored original integrity failed");
      value = decodeOriginalObject(bytes, coordinate);
      cache.set(reference.digest, value);
    }
    if (!root && originalObjectCount(value) !== reference.count)
      return refuse("index and original size disagree");
    return value;
  };
  const root = await load({ digest: parsed.digest, count: 0 }, true);
  if (root.kind !== "range") return refuse("reference does not name an original range");
  const locate = async (
    reference: OriginalRef,
    offset: number,
    kind: "message-index" | "text-index",
  ) => {
    let current = reference,
      relative = offset;
    for (let depth = 0; depth < 16; depth++) {
      const value = await load(current);
      if (kind === "message-index" ? value.kind === "messages" : value.kind === "text")
        return { value, relative };
      if (value.kind !== kind) return refuse("original index points to the wrong content kind");
      let next: OriginalRef | undefined;
      for (const entry of value.entries) {
        if (relative < entry.count) {
          next = entry;
          break;
        }
        relative -= entry.count;
      }
      if (!next) return refuse("original position is outside its index");
      current = next;
    }
    return refuse("original index exceeds its depth budget");
  };
  const { startByte, maxBytes, version } = input.range;
  if (!natural(startByte) || !natural(maxBytes) || maxBytes < 4 || maxBytes > ORIGINAL_TEXT_BYTES)
    return refuse("invalid passage range");
  const page = (content: string, totalBytes: number, sourceVersion: string) => {
    if (version !== undefined && version !== sourceVersion)
      return refuse("original version changed");
    const bytes = utf8.encode(content).length;
    return {
      content,
      bytes,
      range: { startByte, endByte: startByte + bytes, totalBytes, version: sourceVersion },
      source: root.source,
      messageCount: root.messages.count,
    };
  };
  if (parsed.ordinal === null) {
    if (parsed.indexStart >= root.messages.count)
      return refuse("index page is outside the original range");
    const lines = [
      `Original messages behind summary ${root.revision}. Source range ${root.source.rangeDigest}.`,
    ];
    const end = Math.min(parsed.indexStart + FANOUT, root.messages.count);
    for (let ordinal = parsed.indexStart; ordinal < end; ordinal++) {
      const entry = await locate(root.messages, ordinal, "message-index");
      if (entry.value.kind !== "messages" || !entry.value.entries[entry.relative])
        return refuse("invalid original message index");
      const message = entry.value.entries[entry.relative]!;
      lines.push(
        `${ordinal + 1}. ${message.original.id} — ${message.text.count} bytes — read ${parsed.rootMemberId}/original/${ordinal} (version ${message.version})`,
      );
    }
    lines.push(
      end < root.messages.count
        ? `Next index page: ${parsed.rootMemberId}/index/${end}`
        : "End of original range.",
    );
    const bytes = utf8.encode(lines.join("\n"));
    if (startByte > bytes.length) return refuse("byte offset is after this index page");
    let endByte = Math.min(startByte + maxBytes, bytes.length);
    while (endByte < bytes.length && (bytes[endByte]! & 0xc0) === 0x80) endByte--;
    return page(text.decode(bytes.subarray(startByte, endByte)), bytes.length, parsed.digest);
  }
  if (parsed.ordinal >= root.messages.count) return refuse("message is outside the original range");
  const found = await locate(root.messages, parsed.ordinal, "message-index");
  if (found.value.kind !== "messages" || !found.value.entries[found.relative])
    return refuse("invalid original message position");
  const message = found.value.entries[found.relative]!;
  if (startByte > message.text.count) return refuse("byte offset is after this original message");
  let content = "";
  if (startByte < message.text.count) {
    const chunk = await locate(message.text, startByte, "text-index");
    if (chunk.value.kind !== "text") return refuse("message points to invalid original text");
    const bytes = utf8.encode(chunk.value.content);
    let end = Math.min(chunk.relative + maxBytes, bytes.length);
    while (end < bytes.length && (bytes[end]! & 0xc0) === 0x80) end--;
    content = text.decode(bytes.subarray(chunk.relative, end));
  }
  return {
    ...page(content, message.text.count, message.version),
    original: message.original,
    previousMemberId:
      parsed.ordinal > 0 ? `${parsed.rootMemberId}/original/${parsed.ordinal - 1}` : null,
    nextMemberId:
      parsed.ordinal + 1 < root.messages.count
        ? `${parsed.rootMemberId}/original/${parsed.ordinal + 1}`
        : null,
  };
}
